// BHP-1070: create a customer during onboarding. On success the new customer
// becomes the active context and the account bootstrap is invalidated so the
// (now non-empty) customerUuids re-route the user out of onboarding.
import {
  useMutation,
  type UseMutationResult,
  useQueryClient
} from '@tanstack/react-query';
import { hydraKeys } from '../../../shared/api/useHydra';
import { accountKeys } from '../../account/application/useCurrentAccount';
import type { Customer, NewCustomer } from '../domain/customer';
import { useActiveCustomerStore } from './activeCustomerStore';
import { useCustomerGateway } from './gateway';

export function useCreateCustomer(): UseMutationResult<
  Customer,
  unknown,
  NewCustomer
> {
  const gateway = useCustomerGateway();
  const queryClient = useQueryClient();
  const setActiveCustomer = useActiveCustomerStore(
    (state) => state.setActiveCustomer
  );

  return useMutation({
    mutationFn: (input: NewCustomer) => gateway.createCustomer(input),
    onSuccess: async (customer) => {
      setActiveCustomer(customer.uuid);
      // The switch leaves the account and the customer list alone (BHP-1259),
      // and both now hold the new customer.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: accountKeys.current }),
        queryClient.invalidateQueries({
          queryKey: hydraKeys.resource('customers')
        })
      ]);
    }
  });
}
